import {
  ProposalV2,
  ProposalV2Group,
  ProposalV2Product,
} from './proposal-v2.model';
import { PromotionV2CustomerGroup } from './promotion-v2.model';

/**
 * Bidirectional domain mapper for Proposals V2 in Angular promoui.
 * Maps between Angular UI internal form/view representations and clean V2 Proposal API models.
 *
 */
export class ProposalV2Mapper {
  /**
   * Transforms a legacy UI proposal object or form value into a clean ProposalV2 domain model.
   */
  public static toProposalV2(legacy: any): ProposalV2 {
    if (!legacy) {
      throw new Error(
        'Cannot map null or undefined proposal to V2 domain model',
      );
    }

    const rawProposalId = legacy.proposal_id ?? legacy.id ?? null;
    const numericProposalId =
      rawProposalId != null &&
      !isNaN(Number(rawProposalId)) &&
      Number(rawProposalId) > 0
        ? Number(rawProposalId)
        : undefined;

    // Optional linked promotion reference
    const rawPromoId = legacy.promotion_id ?? null;
    const numericPromoId =
      rawPromoId != null && !isNaN(Number(rawPromoId)) && Number(rawPromoId) > 0
        ? Number(rawPromoId)
        : undefined;

    // Normalise end date (strip any extra trailing chars)
    let endDate = legacy.promotion_end_date || legacy.end_date || '';
    if (typeof endDate === 'string' && endDate.endsWith('Z')) {
      endDate = endDate.substring(0, endDate.length - 1);
    }

    const groups: ProposalV2Group[] = Array.isArray(legacy.p_groups)
      ? legacy.p_groups
          .filter((g: any) => g && g.crud_status !== 'DELETE')
          .map((g: any, idx: number) => ({
            id: g.proposal_group_id ?? g.p_group_id ?? g.id ?? undefined,
            group_id: g.proposal_group_id ?? g.p_group_id ?? g.id ?? undefined,
            name:
              g.proposal_group_name ||
              g.p_group_name ||
              g.name ||
              `Group ${idx + 1}`,
            type: (g.proposal_group_type || g.p_group_type || g.type || 'B') as
              | 'B'
              | 'R',
            sequence: Number(
              g.proposal_group_sequence ??
                g.p_group_sequence ??
                g.sequence ??
                idx + 1,
            ),
            required_quantity_in_basket: Number(
              g.required_quantity_in_basket || 0,
            ),
            products: Array.isArray(g.products)
              ? g.products
                  .filter((p: any) => p && p.crud_status !== 'DELETE')
                  .map((p: any) => ({
                    product_no: String(p.product_no ?? p.product_id ?? ''),
                    funding_amount:
                      p.funding_amount != null ? Number(p.funding_amount) : 0,
                    cost_price:
                      p.cost_price != null ? Number(p.cost_price) : undefined,
                    current_retail_price:
                      p.current_retail_price != null
                        ? Number(p.current_retail_price)
                        : undefined,
                    current_promotion_price:
                      p.current_promotion_price != null
                        ? Number(p.current_promotion_price)
                        : undefined,
                    effective_retail_price:
                      p.effective_retail_price != null
                        ? Number(p.effective_retail_price)
                        : undefined,
                    effective_promo_price:
                      p.effective_promo_price != null
                        ? Number(p.effective_promo_price)
                        : undefined,
                    brand_id:
                      p.brand_id != null ? Number(p.brand_id) : undefined,
                    buyer_id:
                      p.buyer_id != null ? String(p.buyer_id) : undefined,
                    supplier_no:
                      p.supplier_no != null ? p.supplier_no : undefined,
                    hero_product_indicator:
                      p.hero_product_indicator != null
                        ? String(p.hero_product_indicator)
                        : undefined,
                    product_status:
                      p.proposal_product_status != null
                        ? String(p.proposal_product_status)
                        : p.product_status != null
                          ? String(p.product_status)
                          : undefined,
                    proposal_product_status:
                      p.proposal_product_status != null
                        ? String(p.proposal_product_status)
                        : p.product_status != null
                          ? String(p.product_status)
                          : undefined,
                    reject_reason_code:
                      p.reject_reason_code != null
                        ? String(p.reject_reason_code)
                        : undefined,
                    reject_reason_message:
                      p.reject_reason_message != null
                        ? String(p.reject_reason_message)
                        : undefined,
                    funding_start_date: p.funding_start_date ?? undefined,
                    funding_end_date: p.funding_end_date ?? undefined,
                    vat_rate:
                      p.vat_rate != null ? Number(p.vat_rate) : undefined,
                  }))
              : [],
            customer_groups: ProposalV2Mapper.mapCustomerGroups(
              g.customer_group,
            ),
          }))
      : [];

    return {
      id: numericProposalId,
      proposal_id: numericProposalId,
      promotion_id: numericPromoId,
      name: legacy.p_name || legacy.proposal_name || legacy.name || '',
      promotion_type: legacy.promotion_type,
      mechanic_type: legacy.mechanic_type,
      start_date: legacy.promotion_start_date || legacy.start_date || '',
      end_date: endDate,
      phase_id:
        legacy.promotion_phase_id != null
          ? Number(legacy.promotion_phase_id)
          : undefined,
      phase_id_custom:
        legacy.phase_id_custom != null
          ? Number(legacy.phase_id_custom)
          : undefined,
      phase_description: legacy.phase_description ?? undefined,
      sub_type: legacy.promotion_sub_type ?? legacy.sub_type ?? undefined,
      sub_type_name:
        legacy.promotion_sub_type_name ?? legacy.sub_type_name ?? undefined,
      webshop: legacy.webshop || 'UK_OCADO',
      status: legacy.p_status_id || legacy.status || 'Draft',
      status_name: legacy.p_status_name ?? legacy.status_name ?? undefined,
      short_description: legacy.short_description ?? undefined,
      long_description: legacy.long_description ?? undefined,
      short_description_translated:
        legacy.short_description_translated ?? undefined,
      long_description_translated:
        legacy.long_description_translated ?? undefined,
      item_ceiling:
        legacy.item_ceiling != null ? Number(legacy.item_ceiling) : undefined,
      reward_amount:
        legacy.reward_amount != null ? Number(legacy.reward_amount) : undefined,
      trigger_quantity:
        legacy.trigger_quantity != null ? Number(legacy.trigger_quantity) : 1,
      trigger_amount:
        legacy.trigger_amount != null
          ? Number(legacy.trigger_amount)
          : undefined,
      trigger_applicable: legacy.trigger_applicable ?? undefined,
      groups,
      ladders: Array.isArray(legacy.ladders)
        ? legacy.ladders.filter((l: any) => l && l.crud_status !== 'DELETE')
        : [],
      customer_groups: ProposalV2Mapper.mapCustomerGroups(
        legacy.customer_group,
      ),
      coupon_metadata: {
        coupon_display_name: legacy.coupon_display_name ?? null,
        master_coupon_promotion_id: legacy.master_coupon_promotion_id ?? null,
        child_coupon_promotion_id: legacy.child_coupon_promotion_id ?? null,
        total_redemption_limit:
          legacy.total_redemption_limit != null
            ? Number(legacy.total_redemption_limit)
            : null,
        customer_ceiling:
          legacy.customer_ceiling != null
            ? Number(legacy.customer_ceiling)
            : null,
      },
      reject_reason_id: legacy.reject_reason_id ?? undefined,
      reject_reason_description: legacy.reject_reason_description ?? undefined,
      comment_notification_to: legacy.comment_notification_to ?? undefined,
      calc_promo_margin_percent:
        legacy.calc_promo_margin_percent != null
          ? Number(legacy.calc_promo_margin_percent)
          : undefined,
      calc_customer_saving_percent:
        legacy.calc_customer_saving_percent != null
          ? Number(legacy.calc_customer_saving_percent)
          : undefined,
      promo_saving:
        legacy.promo_saving != null ? Number(legacy.promo_saving) : undefined,
      calc_off_promo_margin:
        legacy.calc_off_promo_margin != null
          ? Number(legacy.calc_off_promo_margin)
          : undefined,
      calc_funding_percent:
        legacy.calc_funding_percent != null
          ? Number(legacy.calc_funding_percent)
          : undefined,
      calc_brand_id: legacy.calc_brand_id ?? undefined,
      calc_buyer_id: legacy.calc_buyer_id ?? undefined,
      calc_supplier_id: legacy.calc_supplier_id ?? undefined,
      supplier_no:
        legacy.supplier_no != null
          ? Number(legacy.supplier_no)
          : legacy.calc_supplier_id != null
          ? Number(legacy.calc_supplier_id)
          : undefined,
      supplier_name: legacy.supplier_name ?? undefined,
      calc_hero_product_indicator:
        legacy.calc_hero_product_indicator ?? undefined,
      publish_indicator: legacy.publish_indicator ?? undefined,
      created_by: legacy.created_by ?? undefined,
      updated_by: legacy.updated_by ?? undefined,
      created_at: legacy.created_at ?? undefined,
      updated_at: legacy.updated_at ?? undefined,
      inv_addr: legacy.inv_addr || legacy.proposal_invoice_address || undefined,
    };
  }

  /**
   * Transforms a clean ProposalV2 domain model back into the structure expected by legacy UI components.
   * Guarantees promotion_id is never populated from proposal_id.
   */
  public static toLegacyProposal(v2: ProposalV2): any {
    if (!v2) return v2;

    const propId = v2.proposal_id ?? v2.id ?? null;
    return {
      proposal_id: propId,
      promotion_id: v2.promotion_id ?? null,
      p_name: v2.name,
      proposal_name: v2.name,
      promotion_name: v2.name,
      promotion_type: v2.promotion_type,
      mechanic_type: v2.mechanic_type,
      promotion_start_date: v2.start_date,
      promotion_end_date: v2.end_date,
      promotion_phase_id: v2.phase_id ?? null,
      phase_id_custom: v2.phase_id_custom ?? null,
      phase_description: v2.phase_description ?? null,
      promotion_sub_type: v2.sub_type ?? null,
      promotion_sub_type_name: v2.sub_type_name ?? null,
      webshop: v2.webshop || 'UK_OCADO',
      p_status_id: v2.status || 'Draft',
      status: v2.status || 'Draft',
      p_status_name: v2.status_name ?? null,
      short_description: v2.short_description ?? null,
      long_description: v2.long_description ?? null,
      short_description_translated: v2.short_description_translated ?? null,
      long_description_translated: v2.long_description_translated ?? null,
      item_ceiling: v2.item_ceiling ?? null,
      reward_amount: v2.reward_amount ?? null,
      trigger_quantity: v2.trigger_quantity ?? 1,
      trigger_amount: v2.trigger_amount ?? null,
      trigger_applicable: v2.trigger_applicable ?? null,
      p_groups: (v2.groups || []).map((g) => ({
        proposal_group_id: g.id ?? g.group_id ?? null,
        p_group_id: g.id ?? g.group_id ?? null,
        promotion_group_id: null,
        proposal_group_name: g.name,
        p_group_name: g.name,
        proposal_group_type: g.type,
        p_group_type: g.type,
        proposal_group_sequence: g.sequence,
        p_group_sequence: g.sequence,
        required_quantity_in_basket: g.required_quantity_in_basket,
        products: (g.products || []).map((p) => ({
          product_no: p.product_no,
          funding_amount: p.funding_amount ?? 0,
          cost_price: p.cost_price ?? null,
          current_retail_price: p.current_retail_price ?? null,
          current_promotion_price: p.current_promotion_price ?? null,
          effective_retail_price: p.effective_retail_price ?? null,
          effective_promo_price: p.effective_promo_price ?? null,
          brand_id: p.brand_id ?? null,
          buyer_id: p.buyer_id ?? null,
          supplier_no: p.supplier_no ?? null,
          hero_product_indicator: p.hero_product_indicator ?? null,
          product_status: p.proposal_product_status ?? p.product_status ?? null,
          proposal_product_status:
            p.proposal_product_status ?? p.product_status ?? null,
          reject_reason_code: p.reject_reason_code ?? null,
          reject_reason_message: p.reject_reason_message ?? null,
          funding_start_date: p.funding_start_date ?? null,
          funding_end_date: p.funding_end_date ?? null,
          vat_rate: p.vat_rate ?? null,
        })),
        customer_group: (g.customer_groups || []).map((cg) => ({
          customer_group_id: cg.customer_group_id || null,
          customer_group_name: cg.customer_group_name ?? null,
          proposal_group_id:
            cg.proposal_group_id ?? g.id ?? g.group_id ?? null,
          reward_amount: cg.reward_amount ?? null,
          trigger_quantity: cg.trigger_quantity ?? null,
          trigger_amount: cg.trigger_amount ?? null,
          trigger_applicable: cg.trigger_applicable ?? null,
          ladder_sequence_no: cg.ladder_sequence_no ?? null,
          active_ind: cg.active_ind ?? null,
          ud_product_type: cg.ud_product_type ?? null,
          ud_group_name: cg.ud_group_name ?? null,
          ud_custom_group_name: cg.ud_custom_group_name ?? null,
          ud_target_customers: cg.ud_target_customers ?? null,
          ud_other_target_customers: cg.ud_other_target_customers ?? null,
          specific_customer_numbers: cg.specific_customer_numbers ?? null,
          total_redemption_limit: cg.total_redemption_limit ?? null,
        })),
      })),
      ladders: v2.ladders || [],
      customer_group: v2.customer_groups || [],
      coupon_display_name: v2.coupon_metadata?.coupon_display_name ?? null,
      master_coupon_promotion_id:
        v2.coupon_metadata?.master_coupon_promotion_id ?? null,
      child_coupon_promotion_id:
        v2.coupon_metadata?.child_coupon_promotion_id ?? null,
      total_redemption_limit:
        v2.coupon_metadata?.total_redemption_limit ?? null,
      customer_ceiling: v2.coupon_metadata?.customer_ceiling ?? null,
      reject_reason_id: v2.reject_reason_id ?? null,
      reject_reason_description: v2.reject_reason_description ?? null,
      comment_notification_to: v2.comment_notification_to ?? null,
      calc_promo_margin_percent: v2.calc_promo_margin_percent ?? null,
      calc_customer_saving_percent: v2.calc_customer_saving_percent ?? null,
      promo_saving: v2.promo_saving ?? null,
      calc_off_promo_margin: v2.calc_off_promo_margin ?? null,
      calc_funding_percent: v2.calc_funding_percent ?? null,
      calc_brand_id: v2.calc_brand_id ?? null,
      calc_buyer_id: v2.calc_buyer_id ?? null,
      calc_supplier_id: v2.calc_supplier_id ?? null,
      supplier_no: v2.supplier_no ?? null,
      supplier_name: v2.supplier_name ?? null,
      calc_hero_product_indicator: v2.calc_hero_product_indicator ?? null,
      publish_indicator: v2.publish_indicator ?? null,
      created_by: v2.created_by ?? null,
      updated_by: v2.updated_by ?? null,
      created_at: v2.created_at ?? null,
      updated_at: v2.updated_at ?? null,
      inv_addr: v2.inv_addr || [],
      proposal_invoice_address: v2.inv_addr || [],
    };
  }

  /**
   Maps legacy customer_group rows (UI form / detail response shape) to clean V2 customer groups.
   For coupons the reward, targeting and redemption limit live on the per-group customer group,
   so this must be applied to each p_group as well as to the proposal header.
   */
  private static mapCustomerGroups(list: any): PromotionV2CustomerGroup[] {
    if (!Array.isArray(list)) return [];

    const toNumber = (value: any): number | null =>
      value != null && value !== '' && !isNaN(Number(value))
        ? Number(value)
        : null;

    return list
      .filter((cg: any) => cg && cg.crud_status !== 'DELETE')
      .map((cg: any) => ({
        customer_group_id: Number(cg.customer_group_id || 0),
        customer_group_name: cg.customer_group_name ?? null,
        trigger_quantity: toNumber(cg.trigger_quantity),
        trigger_amount: toNumber(cg.trigger_amount),
        reward_amount: toNumber(cg.reward_amount),
        trigger_applicable: cg.trigger_applicable ?? null,
        ladder_sequence_no: toNumber(cg.ladder_sequence_no),
        proposal_group_id: toNumber(cg.proposal_group_id),
        active_ind: cg.active_ind ?? null,
        ud_product_type: cg.ud_product_type ?? null,
        ud_group_name: cg.ud_group_name ?? null,
        ud_custom_group_name: cg.ud_custom_group_name ?? null,
        ud_target_customers: cg.ud_target_customers ?? null,
        ud_other_target_customers: cg.ud_other_target_customers ?? null,
        specific_customer_numbers: cg.specific_customer_numbers ?? null,
        total_redemption_limit: toNumber(cg.total_redemption_limit),
      }));
  }
}
